<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();

const session = computed(() => store.getters.aiAssistSessionFor(props.conversationId));
const status = computed(() => session.value.status);

const isWorking = computed(
  () => status.value === 'thinking' || status.value === 'streaming'
);
const isError = computed(
  () => status.value === 'failed' || status.value === 'unconfigured'
);
const visible = computed(() => isWorking.value || isError.value);

const workingLabel = computed(() =>
  status.value === 'streaming'
    ? t('CUSTOM.AI_ASSIST.STREAMING')
    : t('CUSTOM.AI_ASSIST.THINKING')
);
const errorLabel = computed(() =>
  status.value === 'unconfigured'
    ? t('CUSTOM.AI_ASSIST.UNCONFIGURED')
    : t('CUSTOM.AI_ASSIST.FAILED')
);

const onDismiss = () => {
  store.dispatch('aiAssistDismiss', { conversationId: props.conversationId });
};
</script>

<template>
  <div v-if="visible" class="px-3 pt-2">
    <div
      v-if="isWorking"
      class="flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-n-brand/10 border border-n-brand/30"
    >
      <span class="inline-flex items-center gap-2 text-xs font-medium text-n-brand">
        <span
          class="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin"
        />
        {{ workingLabel }}
      </span>
    </div>

    <div
      v-else
      class="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-n-amber-3 border border-n-amber-7"
    >
      <span class="text-xs font-medium text-n-amber-11">{{ errorLabel }}</span>
      <button
        type="button"
        class="px-2 py-0.5 text-xs font-semibold text-n-amber-11 rounded hover:bg-n-amber-4 cursor-pointer"
        @click="onDismiss"
      >
        {{ t('CUSTOM.AI_ASSIST.DISMISS') }}
      </button>
    </div>
  </div>
</template>
